import React, { useEffect } from 'react'
import type { WithStyles, Theme } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { DropzoneProps as ReactDropzoneProps, DropzoneState, DropzoneRef } from 'react-dropzone'
import ReactDropzone from 'react-dropzone'
import classnames from 'classnames'

import AddIcon from '@material-ui/icons/Add'
import Button from '../Button'

/**
 * react-dropzone's default `display: none` file input gives iOS Safari no rect to
 * anchor its photo-picker context menu to, so WebKit "lifts" a darkened snapshot
 * of the whole dropzone that lingers as a giant dark circle (#20634). Keeping the
 * input rendered but invisible at 1x1px gives the lift animation an effectively
 * empty preview while staying hidden and non-interactive everywhere else.
 *
 * iPadOS goes further: it presents the picker as a popover and "lifts" a preview of
 * the tapped element regardless of the input rect, so the 1x1px input alone wasn't
 * enough there. The WebKit suppressors below disable the callout/highlight/selection
 * that drive that lift. The same suppressors must also be set on the actual tapped
 * container (the dropzone root / upload button), since iPad lifts what the user taps.
 */
export const hiddenFileInputStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  opacity: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  WebkitTapHighlightColor: 'transparent'
}

/**
 * Default `accept` filter for media uploads (file viewer / add-file dialog).
 * Mirrors the file types we render downstream: images, video, and PDFs.
 * Kept in one place so adding a type (e.g. `audio/*`) is a single edit.
 *
 * `.heic`/`.heif`/`.pdf` are listed explicitly, by extension, alongside their
 * MIME entries (`image/*`, `application/pdf`). `react-dropzone`'s accept check
 * (`attr-accept`) matches a MIME entry against the file's *reported* MIME
 * type — and several browsers (notably Chrome on macOS; see the `rejectDrag`
 * comment below, and `normalizeFileMimeType` in `@paintscout/util/s3`) hand
 * HEIC/HEIF/PDF files to the page with an empty `file.type`, which can't
 * match `image/*`/`application/pdf` at all. An extension entry is matched
 * against the *filename* instead, so it still accepts the file regardless of
 * what MIME type (if any) the browser reported. Without `.pdf` here, a PDF
 * hitting this quirk is rejected by the Dropzone itself, before
 * `normalizeFileMimeType`'s own empty-type backfill ever runs.
 */
export const DEFAULT_FILE_ACCEPT = 'image/*,video/*,application/pdf,.heic,.heif,.pdf'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      width: '100%',
      height: '100%',
      // iPad lifts a preview of the tapped element; suppress it (#20634).
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none'
    },
    uploadButton: {},
    buttonFullHeight: {
      height: '100%'
    },
    acceptDrop: {},
    activeDrop: {
      borderColor: `${theme.palette.primary.main}`
    },
    rejectDrop: {
      '& $uploadButton': {
        borderColor: theme.palette.error.main,
        padding: 0
      }
    }
  })

export interface DropzoneProps extends WithStyles<typeof styles>, ReactDropzoneProps {
  style?: React.CSSProperties
  className?: string
  text?: string

  /**
   * Makes the button the full height of the dropzone
   *
   * (you may _not_ want this if you want to use a normal sized 'outlined' variant button and keep the size consistent)
   */
  buttonFullHeight?: boolean

  shouldRenderPreview?: boolean
  dropzoneRef?: React.RefObject<DropzoneRef>
  renderPreview?: (dropzoneArgs: DropzoneState) => any
  onError?: (files: File[], errors: string[]) => any
}

function Dropzone({
  classes,
  className,
  text,
  shouldRenderPreview,
  renderPreview,
  style,
  dropzoneRef,
  buttonFullHeight,
  maxSize,
  onError,
  disabled,
  ...dropzoneProps
}: DropzoneProps) {
  function handleDropRejected(files: File[], event) {
    if (onError) {
      onError(
        files,
        files.map((file) => {
          let error = 'FILE_INVALID'
          if (file.size > maxSize) {
            error = 'LIMIT_FILE_SIZE' // matches multer error response for exceeding file size
          }

          return error
        })
      )
    }

    if (dropzoneProps.onDropRejected) {
      return dropzoneProps.onDropRejected(files, event)
    }
  }

  useEffect(() => {
    // Block browser drag & drop behavior so they are not overridden
    const preventEventDefault = (e) => {
      e.preventDefault()
    }
    window.addEventListener('dragenter', preventEventDefault, false)
    window.addEventListener('dragover', preventEventDefault)
    window.addEventListener('drop', preventEventDefault)

    return () => {
      window.removeEventListener('dragenter', preventEventDefault)
      window.removeEventListener('dragover', preventEventDefault)
      window.removeEventListener('drop', preventEventDefault)
    }
  }, [])

  return (
    <ReactDropzone
      {...dropzoneProps}
      maxSize={maxSize}
      onDropRejected={handleDropRejected}
      ref={dropzoneRef}
      disabled={disabled}
    >
      {(dropzoneArgs) => {
        const { getRootProps, getInputProps, isDragAccept, isDragReject, isDragActive, draggedFiles } = dropzoneArgs
        // Need to second check this as chrome doesnt actually populate these types and rejects everything
        const rejectDrag = isDragReject && draggedFiles.filter((file) => file.type !== '').length > 0
        const buttonMessage = rejectDrag ? 'Invalid File Type' : text || 'Upload'
        return (
          <div
            {...getRootProps()}
            className={classnames({
              [classes.root]: true,
              [className]: !!className,
              [classes.acceptDrop]: isDragAccept,
              [classes.rejectDrop]: rejectDrag,
              [classes.activeDrop]: isDragActive
            })}
            style={style}
          >
            <input {...getInputProps({ style: hiddenFileInputStyle })} />
            {shouldRenderPreview && renderPreview ? (
              renderPreview(dropzoneArgs)
            ) : (
              <Button
                className={classnames({
                  [classes.uploadButton]: true,
                  [classes.buttonFullHeight]: buttonFullHeight,
                  [classes.activeDrop]: isDragActive
                })}
                icon={disabled ? null : AddIcon}
                variant="outlined"
                fullWidth={true}
                disabled={disabled}
              >
                {buttonMessage}
              </Button>
            )}
          </div>
        )
      }}
    </ReactDropzone>
  )
}

Dropzone.defaultProps = {
  maxSize: 1024 * 200000 // 100mb
}

export default withStyles(styles)(Dropzone)
